import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleAlert, RefreshCw, SquareArrowOutUpRight } from 'lucide-react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui'
import { ResponseThread, ThreadSyncStatus } from '@/schema'

import { useTeamworkSyncStatus } from '../hooks/useTeamworkSyncStatus'

type TTeamworkSyncStatusProps = {
  thread: ResponseThread
}

export const TeamworkSyncStatus: FC<TTeamworkSyncStatusProps> = (props) => {
  const { thread } = props

  const { t } = useTranslation()

  const { threadSyncStatus, retryThreadSync, teamworkSyncUrl } =
    useTeamworkSyncStatus({
      thread,
    })

  switch (threadSyncStatus) {
    case ThreadSyncStatus.SYNCED: {
      if (!teamworkSyncUrl) return null

      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <a
              href={teamworkSyncUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-foreground-fg-quaternary-400"
              onClick={(event) => {
                // Prevent triggering parent comment click
                event.stopPropagation()
              }}
              aria-label={t('tflViewer.teamworkSyncStatus.openExternal')}
            >
              <SquareArrowOutUpRight className="size-4" />
            </a>
          </TooltipTrigger>
          <TooltipContent side="left">
            {t('tflViewer.teamworkSyncStatus.openExternal')}
          </TooltipContent>
        </Tooltip>
      )
    }

    case ThreadSyncStatus.SYNCING:
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <div className="inline-flex items-center gap-1 text-foreground-fg-disabled-subtle">
              <RefreshCw className="size-4" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="left">
            {t('tflViewer.teamworkSyncStatus.syncing')}
          </TooltipContent>
        </Tooltip>
      )
    case ThreadSyncStatus.FAILED:
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={(e) => {
                // Prevent triggering parent comment click
                e.stopPropagation()
                retryThreadSync()
              }}
              className="inline-flex items-center gap-1 p-0 bg-transparent border-none cursor-pointer text-foreground-fg-error-secondary"
              aria-label={t('tflViewer.teamworkSyncStatus.resyncAriaLabel')}
            >
              <CircleAlert className="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">
            {t('tflViewer.teamworkSyncStatus.resyncFailed')}
          </TooltipContent>
        </Tooltip>
      )

    case ThreadSyncStatus.NOT_SYNCED:
    default:
      return null
  }
}
